import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, X, Copy, Power, Pencil, Layers } from "lucide-react";
import { brl } from "@/pages/portal/Dashboard";

const EMPTY = {
  material: "", thickness_mm: "", grade: "", price_per_m2: "", material_loss_pct: "",
  sheet_width_mm: "", sheet_length_mm: "", sheet_unit: "mm",
  price_per_kg: "", price_per_sheet: "", supplier: "",
  services: {
    plasma: { enabled: false, price_per_meter: "", price_per_pierce: "", process_loss_pct: "", setup: "" },
    laser: { enabled: false, price_per_meter: "", price_per_pierce: "", process_loss_pct: "", setup: "" },
    dobra: { enabled: false, price_per_meter: "" },
    jateamento: { enabled: false },
  },
  active: true,
};

const CUT_FIELDS = [
  ["price_per_meter", "Valor por metro de corte (R$)"],
  ["price_per_pierce", "Valor por furação/entrada (R$)"],
  ["process_loss_pct", "Perda do processo (%)"],
  ["setup", "Setup (R$)"],
];

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-[10px] uppercase tracking-wider text-slate-500";

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
const fromApi = (c) => ({
  id: c.id,
  material: c.material || "", thickness_mm: c.thickness_mm ?? "", grade: c.grade || "",
  price_per_m2: c.price_per_m2 ?? "", material_loss_pct: c.material_loss_pct ?? "",
  sheet_width_mm: c.sheet_width_mm ?? "", sheet_length_mm: c.sheet_length_mm ?? "",
  sheet_unit: c.sheet_unit || "mm",
  price_per_kg: c.price_per_kg ?? "", price_per_sheet: c.price_per_sheet ?? "", supplier: c.supplier || "",
  services: {
    plasma: { enabled: !!c.services?.plasma?.enabled, price_per_meter: c.services?.plasma?.price_per_meter ?? "", price_per_pierce: c.services?.plasma?.price_per_pierce ?? "", process_loss_pct: c.services?.plasma?.process_loss_pct ?? "", setup: c.services?.plasma?.setup ?? "" },
    laser: { enabled: !!c.services?.laser?.enabled, price_per_meter: c.services?.laser?.price_per_meter ?? "", price_per_pierce: c.services?.laser?.price_per_pierce ?? "", process_loss_pct: c.services?.laser?.process_loss_pct ?? "", setup: c.services?.laser?.setup ?? "" },
    dobra: { enabled: !!c.services?.dobra?.enabled, price_per_meter: c.services?.dobra?.price_per_meter ?? "" },
    jateamento: { enabled: !!c.services?.jateamento?.enabled },
  },
  active: c.active !== false,
});

export default function AdminPrecos() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jat, setJat] = useState(null);
  const [jatSaving, setJatSaving] = useState(false);
  const [dobraMax, setDobraMax] = useState("");
  const [dobraLen, setDobraLen] = useState("");
  const [dobraSaving, setDobraSaving] = useState(false);
  const [dobraPrices, setDobraPrices] = useState([]);
  const [dobraForm, setDobraForm] = useState(null);
  const [gravPrices, setGravPrices] = useState([]);
  const [gravForm, setGravForm] = useState(null);

  const load = () => api.get("/sheet-configs").then(({ data }) => setItems(data)).catch(() => setItems([]));
  const loadDobraPrices = () => api.get("/admin/dobra-prices").then(({ data }) => setDobraPrices(data)).catch(() => setDobraPrices([]));
  const loadGravPrices = () => api.get("/admin/gravacao-prices").then(({ data }) => setGravPrices(data)).catch(() => setGravPrices([]));
  useEffect(() => {
    load();
    api.get("/admin/jateamento-config").then(({ data }) => setJat({
      price_per_m2: data.price_per_m2 ?? "",
      minimum_value: data.minimum_value ?? "",
      extras: data.extras ?? "",
      difficulty_percents: { baixa: 0, media: 15, alta: 30, muito_alta: 50, ...(data.difficulty_percents || {}) },
      booth: {
        max_length_mm: data.booth?.max_length_mm ?? "",
        max_width_mm: data.booth?.max_width_mm ?? "",
        max_height_mm: data.booth?.max_height_mm ?? "",
        max_weight_kg: data.booth?.max_weight_kg ?? "",
      },
      allow_equivalent_faces: !!data.allow_equivalent_faces,
    })).catch(() => setJat(null));
    api.get("/admin/dobra-config").then(({ data }) => {
      setDobraMax(data.max_thickness_mm ?? "");
      setDobraLen(data.max_bend_length_mm ?? "");
    }).catch(() => {});
    loadDobraPrices();
    loadGravPrices();
  }, []);

  const setSvc = (svc, k, v) =>
    setForm({ ...form, services: { ...form.services, [svc]: { ...form.services[svc], [k]: v } } });

  const save = async (e) => {
    e.preventDefault();
    if (!form.material.trim()) { toast.error("Informe o material."); return; }
    if (!(Number(form.thickness_mm) > 0)) { toast.error("Espessura deve ser maior que zero (decimais permitidos, ex.: 6.35)."); return; }
    if (!(Number(form.price_per_m2) >= 0)) { toast.error("Informe o valor do material por m²."); return; }
    setSaving(true);
    const cut = (s) => ({
      enabled: !!s.enabled, price_per_meter: numOrNull(s.price_per_meter),
      price_per_pierce: numOrNull(s.price_per_pierce), process_loss_pct: numOrNull(s.process_loss_pct),
      setup: numOrNull(s.setup),
    });
    const payload = {
      material: form.material.trim(), thickness_mm: Number(form.thickness_mm), grade: form.grade.trim(),
      price_per_m2: Number(form.price_per_m2), material_loss_pct: Number(form.material_loss_pct || 0),
      sheet_width_mm: numOrNull(form.sheet_width_mm), sheet_length_mm: numOrNull(form.sheet_length_mm),
      sheet_unit: form.sheet_unit,
      price_per_kg: numOrNull(form.price_per_kg), price_per_sheet: numOrNull(form.price_per_sheet),
      supplier: form.supplier.trim(),
      services: { plasma: cut(form.services.plasma), laser: cut(form.services.laser),
                  dobra: { enabled: !!form.services.dobra.enabled, price_per_meter: numOrNull(form.services.dobra.price_per_meter) },
                  jateamento: { enabled: !!form.services.jateamento?.enabled } },
      active: !!form.active,
    };
    try {
      if (form.id) await api.put(`/sheet-configs/${form.id}`, payload);
      else await api.post("/sheet-configs", payload);
      toast.success("Configuração de chapa salva.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (c) => {
    try {
      await api.post(`/sheet-configs/${c.id}/duplicate`);
      toast.success("Chapa duplicada (criada inativa — ajuste e ative).");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveDobra = async (e) => {
    e.preventDefault();
    setDobraSaving(true);
    try {
      await api.put("/admin/dobra-config", { max_thickness_mm: numOrNull(dobraMax), max_bend_length_mm: numOrNull(dobraLen) });
      toast.success("Capacidade de dobra salva.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDobraSaving(false);
    }
  };

  const saveDobraPrice = async (e) => {
    e.preventDefault();
    if (!dobraForm.material.trim()) { toast.error("Informe o material."); return; }
    if (!(Number(dobraForm.thickness_mm) > 0)) { toast.error("Espessura deve ser maior que zero (decimais permitidos, ex.: 1.20)."); return; }
    if (!(Number(dobraForm.price_per_meter) >= 0)) { toast.error("Informe o valor por metro linear de dobra."); return; }
    setSaving(true);
    const payload = {
      material: dobraForm.material.trim(), thickness_mm: Number(dobraForm.thickness_mm),
      price_per_meter: numOrNull(dobraForm.price_per_meter), minimum_value: numOrNull(dobraForm.minimum_value),
      active: !!dobraForm.active,
    };
    try {
      if (dobraForm.id) await api.put(`/admin/dobra-prices/${dobraForm.id}`, payload);
      else await api.post("/admin/dobra-prices", payload);
      toast.success("Preço de dobra salvo.");
      setDobraForm(null);
      loadDobraPrices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteDobraPrice = async (p) => {
    if (!window.confirm(`Excluir o preço de dobra ${p.material} ${p.thickness_mm} mm?`)) return;
    try {
      await api.delete(`/admin/dobra-prices/${p.id}`);
      toast.success("Cadastro de dobra excluído.");
      loadDobraPrices();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveGravPrice = async (e) => {
    e.preventDefault();
    if (!gravForm.machine.trim()) { toast.error("Informe a máquina de gravação."); return; }
    if (!gravForm.material.trim()) { toast.error("Informe o material de gravação."); return; }
    if (!gravForm.depth.trim()) { toast.error("Informe a profundidade da gravação."); return; }
    if (!(Number(gravForm.price_per_cm2) >= 0)) { toast.error("Informe o valor por cm²."); return; }
    setSaving(true);
    const payload = {
      machine: gravForm.machine.trim(), material: gravForm.material.trim(), depth: gravForm.depth.trim(),
      price_per_cm2: numOrNull(gravForm.price_per_cm2), minimum_value: numOrNull(gravForm.minimum_value),
      setup: numOrNull(gravForm.setup), active: !!gravForm.active,
    };
    try {
      if (gravForm.id) await api.put(`/admin/gravacao-prices/${gravForm.id}`, payload);
      else await api.post("/admin/gravacao-prices", payload);
      toast.success("Preço de gravação salvo.");
      setGravForm(null);
      loadGravPrices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteGravPrice = async (p) => {
    if (!window.confirm(`Excluir o preço de gravação ${p.machine} / ${p.material} / ${p.depth}?`)) return;
    try {
      await api.delete(`/admin/gravacao-prices/${p.id}`);
      toast.success("Cadastro de gravação excluído.");
      loadGravPrices();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveJat = async (e) => {
    e.preventDefault();
    setJatSaving(true);
    try {
      await api.put("/admin/jateamento-config", {
        price_per_m2: numOrNull(jat.price_per_m2),
        minimum_value: numOrNull(jat.minimum_value),
        extras: numOrNull(jat.extras),
        difficulty_percents: Object.fromEntries(Object.entries(jat.difficulty_percents).map(([k, v]) => [k, Number(v) || 0])),
        booth: Object.fromEntries(Object.entries(jat.booth).map(([k, v]) => [k, Number(v) || 0])),
        allow_equivalent_faces: !!jat.allow_equivalent_faces,
      });
      toast.success("Configuração de Jateamento salva.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setJatSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      const full = fromApi(c);
      await api.put(`/sheet-configs/${c.id}`, {
        material: full.material, thickness_mm: Number(full.thickness_mm), grade: full.grade,
        price_per_m2: Number(full.price_per_m2), material_loss_pct: Number(full.material_loss_pct || 0),
        sheet_width_mm: numOrNull(full.sheet_width_mm), sheet_length_mm: numOrNull(full.sheet_length_mm),
        sheet_unit: full.sheet_unit,
        price_per_kg: numOrNull(full.price_per_kg), price_per_sheet: numOrNull(full.price_per_sheet),
        supplier: full.supplier.trim(),
        services: {
          plasma: { ...full.services.plasma, price_per_meter: numOrNull(full.services.plasma.price_per_meter), price_per_pierce: numOrNull(full.services.plasma.price_per_pierce), process_loss_pct: numOrNull(full.services.plasma.process_loss_pct), setup: numOrNull(full.services.plasma.setup) },
          laser: { ...full.services.laser, price_per_meter: numOrNull(full.services.laser.price_per_meter), price_per_pierce: numOrNull(full.services.laser.price_per_pierce), process_loss_pct: numOrNull(full.services.laser.process_loss_pct), setup: numOrNull(full.services.laser.setup) },
          dobra: { enabled: full.services.dobra.enabled, price_per_meter: numOrNull(full.services.dobra.price_per_meter) },
          jateamento: { enabled: !!full.services.jateamento?.enabled },
        },
        active: !c.active,
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const svcBadge = (on, label) => (
    <span className={`text-[9px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${on ? "text-emerald-400 border-emerald-900/60" : "text-slate-600 border-slate-800"}`}>{label}</span>
  );

  if (!items) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div data-testid="admin-prices-page" className="max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-3xl font-extrabold uppercase">Preços</h1>
          <p className="mt-1 text-sm text-slate-500">
            Regras de precificação por chapa: cada combinação material + espessura + liga tem seus próprios valores de material, corte e dobra.
          </p>
        </div>
        <button data-testid="price-new-button" onClick={() => setForm({ ...EMPTY, services: { plasma: { ...EMPTY.services.plasma }, laser: { ...EMPTY.services.laser }, dobra: { ...EMPTY.services.dobra }, jateamento: { ...EMPTY.services.jateamento } } })}
          className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">
          <Plus className="w-4 h-4" /> Nova chapa
        </button>
      </div>

      <div className="mt-6 card-tactical rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="prices-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-4 py-3">Material</th>
              <th className="px-4 py-3">Espessura</th>
              <th className="px-4 py-3">Liga</th>
              <th className="px-4 py-3">R$/m²</th>
              <th className="px-4 py-3">Perda</th>
              <th className="px-4 py-3">Plasma</th>
              <th className="px-4 py-3">Laser</th>
              <th className="px-4 py-3">Dobra</th>
              <th className="px-4 py-3">Jateamento</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-900/40" data-testid={`price-row-${c.id}`}>
                <td className="px-4 py-3 text-slate-200 font-medium">
                  <div className="flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-orange-500" strokeWidth={1.5} />{c.material}</div>
                  {c.sheet_area_m2 && <div className="text-[10px] text-slate-600 mono mt-0.5">chapa {c.sheet_width_mm}×{c.sheet_length_mm} mm · {c.sheet_area_m2} m²</div>}
                  {c.supplier && <div className="text-[10px] text-slate-600 mt-0.5">{c.supplier}</div>}
                </td>
                <td className="px-4 py-3 mono text-orange-400">{c.thickness_mm} mm</td>
                <td className="px-4 py-3 text-slate-400">{c.grade || "—"}</td>
                <td className="px-4 py-3 mono">{brl(c.price_per_m2)}</td>
                <td className="px-4 py-3 mono text-slate-400">{c.material_loss_pct ?? 0}%</td>
                <td className="px-4 py-3">{svcBadge(c.services?.plasma?.enabled, c.services?.plasma?.enabled ? `${brl(c.services.plasma.price_per_meter)}/m` : "off")}</td>
                <td className="px-4 py-3">{svcBadge(c.services?.laser?.enabled, c.services?.laser?.enabled ? `${brl(c.services.laser.price_per_meter)}/m` : "off")}</td>
                <td className="px-4 py-3">{svcBadge(c.services?.dobra?.enabled, c.services?.dobra?.enabled ? `${brl(c.services.dobra.price_per_meter)}/m` : "off")}</td>
                <td className="px-4 py-3"><span data-testid={`price-jat-${c.id}`}>{svcBadge(c.services?.jateamento?.enabled, c.services?.jateamento?.enabled ? "on" : "off")}</span></td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider ${c.active !== false ? "text-emerald-400" : "text-slate-600"}`} data-testid={`price-status-${c.id}`}>
                    {c.active !== false ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2.5">
                    <button onClick={() => setForm(fromApi(c))} data-testid={`price-edit-${c.id}`} className="text-slate-500 hover:text-orange-400 transition-colors" aria-label="Editar"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => duplicate(c)} data-testid={`price-duplicate-${c.id}`} className="text-slate-500 hover:text-orange-400 transition-colors" aria-label="Duplicar"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => toggle(c)} data-testid={`price-toggle-${c.id}`} className={`${c.active !== false ? "text-emerald-500 hover:text-slate-400" : "text-slate-600 hover:text-emerald-400"} transition-colors`} aria-label="Ativar/Inativar"><Power className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-slate-600" data-testid="prices-empty">
                Nenhuma chapa cadastrada. Clique em "+ Nova chapa" para começar.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 card-tactical rounded-md p-6" data-testid="dobra-config-section">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Dobra — capacidade</h2>
        <p className="mt-1 text-xs text-slate-500">
          Capacidade da dobradeira. A espessura máxima alimenta o rótulo "Dobra de Chapas (até X mm)" do site; comprimentos de dobra acima do limite marcam o item como "Necessita análise técnica". 0 ou vazio = sem limite.
        </p>
        <form onSubmit={saveDobra} className="mt-3 flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className={labelCls}>Espessura máx. de dobra (mm)</label>
            <input type="number" step="0.01" min="0" data-testid="dobra-max-thickness" value={dobraMax}
              onChange={(e) => setDobraMax(e.target.value)} className={inputCls} placeholder="Ex.: 2.0" />
          </div>
          <div className="w-56">
            <label className={labelCls}>Comprimento máx. de dobra (mm)</label>
            <input type="number" step="0.01" min="0" data-testid="dobra-max-length" value={dobraLen}
              onChange={(e) => setDobraLen(e.target.value)} className={inputCls} placeholder="Ex.: 3000" />
          </div>
          <button data-testid="dobra-save-button" disabled={dobraSaving}
            className="inline-flex items-center gap-2 pill-accent rounded-md px-5 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
            {dobraSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
          </button>
        </form>
      </div>

      <div className="mt-6 card-tactical rounded-md p-6" data-testid="dobra-prices-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="display text-lg font-bold uppercase tracking-wide">Dobra — Materiais/Preços</h2>
            <p className="mt-1 text-xs text-slate-500">
              Preço por metro linear de dobra por combinação material + espessura (sem liga). Orçamentos já calculados preservam o snapshot de preços do momento do cálculo.
            </p>
          </div>
          <button data-testid="dobra-price-new-button"
            onClick={() => setDobraForm({ id: null, material: "", thickness_mm: "", price_per_meter: "", minimum_value: "", active: true })}
            className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition">
            <Plus className="w-4 h-4" /> Novo material de dobra
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="dobra-prices-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-3 py-2">Material</th>
                <th className="px-3 py-2">Espessura</th>
                <th className="px-3 py-2">R$/metro linear</th>
                <th className="px-3 py-2">Valor mínimo</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {dobraPrices.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60" data-testid={`dobra-price-row-${p.id}`}>
                  <td className="px-3 py-2.5 text-slate-200">{p.material}</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.thickness_mm} mm</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.price_per_meter != null ? `${brl(p.price_per_meter)}/m` : "—"}</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.minimum_value != null ? brl(p.minimum_value) : "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${p.active !== false ? "text-emerald-400 border-emerald-900/60" : "text-slate-500 border-slate-700"}`}>
                      {p.active !== false ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button data-testid={`dobra-price-edit-${p.id}`}
                      onClick={() => setDobraForm({ id: p.id, material: p.material, thickness_mm: p.thickness_mm ?? "", price_per_meter: p.price_per_meter ?? "", minimum_value: p.minimum_value ?? "", active: p.active !== false })}
                      className="text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 transition-colors mr-3">
                      <Pencil className="w-3.5 h-3.5 inline" /> Editar
                    </button>
                    <button data-testid={`dobra-price-delete-${p.id}`} onClick={() => deleteDobraPrice(p)}
                      className="text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5 inline" /> Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {!dobraPrices.length && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500" data-testid="dobra-prices-empty">
                  Nenhum material de dobra cadastrado. Clique em "+ Novo material de dobra" para começar.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 card-tactical rounded-md p-6" data-testid="gravacao-prices-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="display text-lg font-bold uppercase tracking-wide">Gravação a Laser — Materiais/Preços</h2>
            <p className="mt-1 text-xs text-slate-500">
              Valor por cm² por combinação máquina + material + profundidade. Máquinas, materiais e profundidades cadastrados aqui alimentam automaticamente o formulário público de orçamento.
            </p>
          </div>
          <button data-testid="grav-price-new-button"
            onClick={() => setGravForm({ id: null, machine: "", material: "", depth: "", price_per_cm2: "", minimum_value: "", setup: "", active: true })}
            className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition">
            <Plus className="w-4 h-4" /> Nova combinação de gravação
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="grav-prices-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-3 py-2">Máquina</th>
                <th className="px-3 py-2">Material</th>
                <th className="px-3 py-2">Profundidade</th>
                <th className="px-3 py-2">R$/cm²</th>
                <th className="px-3 py-2">Mínimo</th>
                <th className="px-3 py-2">Setup</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {gravPrices.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60" data-testid={`grav-price-row-${p.id}`}>
                  <td className="px-3 py-2.5 text-slate-200">{p.machine}</td>
                  <td className="px-3 py-2.5 text-slate-300">{p.material}</td>
                  <td className="px-3 py-2.5 text-slate-300">{p.depth}</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.price_per_cm2 != null ? `${brl(p.price_per_cm2)}/cm²` : "—"}</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.minimum_value != null ? brl(p.minimum_value) : "—"}</td>
                  <td className="px-3 py-2.5 mono text-slate-300">{p.setup != null ? brl(p.setup) : "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${p.active !== false ? "text-emerald-400 border-emerald-900/60" : "text-slate-500 border-slate-700"}`}>
                      {p.active !== false ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`grav-price-edit-${p.id}`}
                      onClick={() => setGravForm({ id: p.id, machine: p.machine, material: p.material, depth: p.depth, price_per_cm2: p.price_per_cm2 ?? "", minimum_value: p.minimum_value ?? "", setup: p.setup ?? "", active: p.active !== false })}
                      className="text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 transition-colors mr-3">
                      <Pencil className="w-3.5 h-3.5 inline" /> Editar
                    </button>
                    <button data-testid={`grav-price-delete-${p.id}`} onClick={() => deleteGravPrice(p)}
                      className="text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5 inline" /> Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {!gravPrices.length && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500" data-testid="grav-prices-empty">
                  Nenhuma combinação de gravação cadastrada. Clique em "+ Nova combinação de gravação" para começar.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 card-tactical rounded-md p-6" data-testid="jat-config-section">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Jateamento — configuração do serviço</h2>
        <p className="mt-1 text-xs text-slate-500">
          Valores e limites editáveis. Orçamentos já calculados preservam o snapshot de preços do momento do cálculo — mudanças aqui não alteram orçamentos existentes.
        </p>
        {!jat ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-600" /></div>
        ) : (
          <form onSubmit={saveJat} data-testid="jat-config-form">
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Valor por m² (R$)</label>
                <input type="number" step="0.01" min="0" data-testid="jat-input-price-m2" value={jat.price_per_m2}
                  onChange={(e) => setJat({ ...jat, price_per_m2: e.target.value })} className={inputCls} placeholder="Ex.: 20.00" />
              </div>
              <div>
                <label className={labelCls}>Valor mínimo do serviço (R$)</label>
                <input type="number" step="0.01" min="0" data-testid="jat-input-minimum" value={jat.minimum_value}
                  onChange={(e) => setJat({ ...jat, minimum_value: e.target.value })} className={inputCls} placeholder="0 = sem mínimo" />
              </div>
              <div>
                <label className={labelCls}>Adicionais (R$)</label>
                <input type="number" step="0.01" min="0" data-testid="jat-input-extras" value={jat.extras}
                  onChange={(e) => setJat({ ...jat, extras: e.target.value })} className={inputCls} placeholder="0" />
              </div>
            </div>
            <h3 className="display mt-6 text-sm font-bold uppercase tracking-wide text-slate-400">Acréscimo por nível de dificuldade (%)</h3>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[["baixa", "Baixa"], ["media", "Média"], ["alta", "Alta"], ["muito_alta", "Muito alta"]].map(([k, label]) => (
                <div key={k}>
                  <label className={labelCls}>{label} (%)</label>
                  <input type="number" step="0.1" min="0" data-testid={`jat-diff-${k}`} value={jat.difficulty_percents[k]}
                    onChange={(e) => setJat({ ...jat, difficulty_percents: { ...jat.difficulty_percents, [k]: e.target.value } })} className={inputCls} />
                </div>
              ))}
            </div>
            <h3 className="display mt-6 text-sm font-bold uppercase tracking-wide text-slate-400">Capacidade da cabine (0 = sem limite)</h3>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[["max_length_mm", "Comprimento máx. (mm)"], ["max_width_mm", "Largura máx. (mm)"],
                ["max_height_mm", "Altura máx. (mm)"], ["max_weight_kg", "Peso máx. (kg)"]].map(([k, label]) => (
                <div key={k}>
                  <label className={labelCls}>{label}</label>
                  <input type="number" step="0.01" min="0" data-testid={`jat-booth-${k}`} value={jat.booth[k]}
                    onChange={(e) => setJat({ ...jat, booth: { ...jat.booth, [k]: e.target.value } })} className={inputCls} placeholder="0" />
                </div>
              ))}
            </div>
            <label className="mt-5 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="jat-allow-equivalent" checked={!!jat.allow_equivalent_faces}
                onChange={(e) => setJat({ ...jat, allow_equivalent_faces: e.target.checked })} className="accent-orange-500 w-4 h-4" />
              Permitir 7–10 faces como "áreas equivalentes de jateamento" (estimativa pela média das 6 faces)
            </label>
            <button data-testid="jat-save-button" disabled={jatSaving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {jatSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar configuração de jateamento
            </button>
          </form>
        )}
      </div>

      {gravForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setGravForm(null)}>
          <form onSubmit={saveGravPrice} className="card-tactical rounded-md p-6 w-full max-w-xl"
            onClick={(e) => e.stopPropagation()} data-testid="grav-price-form">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-bold uppercase tracking-wide">{gravForm.id ? "Editar preço de gravação" : "Nova combinação de gravação"}</h2>
              <button type="button" onClick={() => setGravForm(null)} data-testid="grav-price-form-cancel" className="text-slate-500 hover:text-slate-300" aria-label="Fechar"><X className="w-4 h-4" /></button>
            </div>
            <datalist id="grav-machines-list">
              {[...new Set([...gravPrices.map((p) => p.machine), "Laser Fiber", "Laser Diodo"])].map((m) => <option key={m} value={m} />)}
            </datalist>
            <datalist id="grav-depths-list">
              {[...new Set([...gravPrices.map((p) => p.depth), "Superficial", "Rasa", "Média", "Funda"])].map((d) => <option key={d} value={d} />)}
            </datalist>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Máquina *</label>
                <input data-testid="grav-input-machine" list="grav-machines-list" value={gravForm.machine}
                  onChange={(e) => setGravForm({ ...gravForm, machine: e.target.value })} className={inputCls} placeholder="Ex.: Laser Fiber" />
              </div>
              <div>
                <label className={labelCls}>Material *</label>
                <input data-testid="grav-input-material" value={gravForm.material}
                  onChange={(e) => setGravForm({ ...gravForm, material: e.target.value })} className={inputCls} placeholder="Ex.: Inox, Madeira, MDF" />
              </div>
              <div>
                <label className={labelCls}>Profundidade *</label>
                <input data-testid="grav-input-depth" list="grav-depths-list" value={gravForm.depth}
                  onChange={(e) => setGravForm({ ...gravForm, depth: e.target.value })} className={inputCls} placeholder="Ex.: Superficial" />
              </div>
              <div>
                <label className={labelCls}>Valor por cm² (R$) *</label>
                <input data-testid="grav-input-price" type="number" step="0.01" min="0" value={gravForm.price_per_cm2}
                  onChange={(e) => setGravForm({ ...gravForm, price_per_cm2: e.target.value })} className={inputCls} placeholder="Ex.: 2.00" />
              </div>
              <div>
                <label className={labelCls}>Valor mínimo (R$)</label>
                <input data-testid="grav-input-minimum" type="number" step="0.01" min="0" value={gravForm.minimum_value}
                  onChange={(e) => setGravForm({ ...gravForm, minimum_value: e.target.value })} className={inputCls} placeholder="0 = sem mínimo" />
              </div>
              <div>
                <label className={labelCls}>Setup (R$) — se aplicável</label>
                <input data-testid="grav-input-setup" type="number" step="0.01" min="0" value={gravForm.setup}
                  onChange={(e) => setGravForm({ ...gravForm, setup: e.target.value })} className={inputCls} placeholder="0 = sem setup" />
              </div>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="grav-input-active" checked={!!gravForm.active}
                onChange={(e) => setGravForm({ ...gravForm, active: e.target.checked })} className="accent-orange-500 w-4 h-4" />
              Cadastro ativo (disponível no orçamento)
            </label>
            <button data-testid="grav-price-save-button" disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar combinação
            </button>
          </form>
        </div>
      )}

      {dobraForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setDobraForm(null)}>
          <form onSubmit={saveDobraPrice} className="card-tactical rounded-md p-6 w-full max-w-xl"
            onClick={(e) => e.stopPropagation()} data-testid="dobra-price-form">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-bold uppercase tracking-wide">{dobraForm.id ? "Editar preço de dobra" : "Novo material de dobra"}</h2>
              <button type="button" onClick={() => setDobraForm(null)} data-testid="dobra-price-form-cancel" className="text-slate-500 hover:text-slate-300" aria-label="Fechar"><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Material *</label>
                <input data-testid="dobra-price-input-material" value={dobraForm.material}
                  onChange={(e) => setDobraForm({ ...dobraForm, material: e.target.value })} className={inputCls} placeholder="Ex.: Aço Carbono" />
              </div>
              <div>
                <label className={labelCls}>Espessura (mm) * — decimais OK</label>
                <input data-testid="dobra-price-input-thickness" type="number" step="0.01" min="0.01" value={dobraForm.thickness_mm}
                  onChange={(e) => setDobraForm({ ...dobraForm, thickness_mm: e.target.value })} className={inputCls} placeholder="Ex.: 1.2, 2.65, 3.0" />
              </div>
              <div>
                <label className={labelCls}>Valor por metro linear de dobra (R$) *</label>
                <input data-testid="dobra-price-input-price" type="number" step="0.01" min="0" value={dobraForm.price_per_meter}
                  onChange={(e) => setDobraForm({ ...dobraForm, price_per_meter: e.target.value })} className={inputCls} placeholder="Ex.: 25.00" />
              </div>
              <div>
                <label className={labelCls}>Valor mínimo (R$)</label>
                <input data-testid="dobra-price-input-minimum" type="number" step="0.01" min="0" value={dobraForm.minimum_value}
                  onChange={(e) => setDobraForm({ ...dobraForm, minimum_value: e.target.value })} className={inputCls} placeholder="0 = sem mínimo" />
              </div>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="dobra-price-input-active" checked={!!dobraForm.active}
                onChange={(e) => setDobraForm({ ...dobraForm, active: e.target.checked })} className="accent-orange-500 w-4 h-4" />
              Cadastro ativo (disponível no orçamento)
            </label>
            <button data-testid="dobra-price-save-button" disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar preço de dobra
            </button>
          </form>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setForm(null)}>
          <form onSubmit={save} className="card-tactical rounded-md p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="price-form">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-bold uppercase tracking-wide">{form.id ? "Editar chapa" : "Nova chapa"}</h2>
              <button type="button" onClick={() => setForm(null)} data-testid="price-form-cancel" className="text-slate-500 hover:text-slate-300" aria-label="Fechar"><X className="w-4 h-4" /></button>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Material *</label>
                <input data-testid="price-input-material" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className={inputCls} placeholder="Ex.: Aço Carbono" />
              </div>
              <div>
                <label className={labelCls}>Espessura (mm) * — decimais OK</label>
                <input data-testid="price-input-thickness" type="number" step="0.01" min="0.01" value={form.thickness_mm} onChange={(e) => setForm({ ...form, thickness_mm: e.target.value })} className={inputCls} placeholder="Ex.: 1.2, 4.75, 6.35" />
              </div>
              <div>
                <label className={labelCls}>Liga / Grau</label>
                <input data-testid="price-input-grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={inputCls} placeholder="Ex.: SAE 1020" />
              </div>
              <div>
                <label className={labelCls}>Valor do material (R$/m²) *</label>
                <input data-testid="price-input-price-m2" type="number" step="0.01" min="0" value={form.price_per_m2} onChange={(e) => setForm({ ...form, price_per_m2: e.target.value })} className={inputCls} placeholder="Ex.: 200.00" />
              </div>
              <div>
                <label className={labelCls}>Perda de material (%)</label>
                <input data-testid="price-input-loss" type="number" step="0.1" min="0" value={form.material_loss_pct} onChange={(e) => setForm({ ...form, material_loss_pct: e.target.value })} className={inputCls} placeholder="Ex.: 10" />
              </div>
              <div>
                <label className={labelCls}>Largura da chapa (mm)</label>
                <input data-testid="price-input-sheet-w" type="number" step="1" min="0" value={form.sheet_width_mm} onChange={(e) => setForm({ ...form, sheet_width_mm: e.target.value })} className={inputCls} placeholder="Ex.: 2000" />
              </div>
              <div>
                <label className={labelCls}>Comprimento da chapa (mm)</label>
                <input data-testid="price-input-sheet-l" type="number" step="1" min="0" value={form.sheet_length_mm} onChange={(e) => setForm({ ...form, sheet_length_mm: e.target.value })} className={inputCls} placeholder="Ex.: 1000" />
              </div>
              <div className="flex items-end pb-2">
                <span className="text-[11px] text-slate-500 mono" data-testid="price-sheet-area">
                  Área da chapa: {form.sheet_width_mm && form.sheet_length_mm
                    ? `${((Number(form.sheet_width_mm) / 1000) * (Number(form.sheet_length_mm) / 1000)).toFixed(2)} m²`
                    : "—"}
                </span>
              </div>
              <div>
                <label className={labelCls}>Preço por kg (R$) — opcional</label>
                <input data-testid="price-input-price-kg" type="number" step="0.01" min="0" value={form.price_per_kg} onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Preço por chapa (R$) — opcional</label>
                <input data-testid="price-input-price-sheet" type="number" step="0.01" min="0" value={form.price_per_sheet} onChange={(e) => setForm({ ...form, price_per_sheet: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Fornecedor — opcional</label>
                <input data-testid="price-input-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className={inputCls} />
              </div>
            </div>

            <div className="mt-5 grid md:grid-cols-3 gap-4">
              {["plasma", "laser"].map((svc) => (
                <div key={svc} className="rounded-md border border-slate-800 p-4" data-testid={`price-svc-${svc}`}>
                  <label className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200 cursor-pointer">
                    <input type="checkbox" data-testid={`price-svc-${svc}-enabled`} checked={form.services[svc].enabled}
                      onChange={(e) => setSvc(svc, "enabled", e.target.checked)} className="accent-orange-500 w-4 h-4" />
                    Corte {svc === "plasma" ? "Plasma" : "Laser"}
                  </label>
                  {form.services[svc].enabled && (
                    <div className="mt-3 space-y-3">
                      {CUT_FIELDS.map(([k, label]) => (
                        <div key={k}>
                          <label className={labelCls}>{label}</label>
                          <input type="number" step="0.01" min="0" data-testid={`price-svc-${svc}-${k}`}
                            value={form.services[svc][k]} onChange={(e) => setSvc(svc, k, e.target.value)} className={inputCls} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="rounded-md border border-slate-800 p-4" data-testid="price-svc-dobra">
                <label className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200 cursor-pointer">
                  <input type="checkbox" data-testid="price-svc-dobra-enabled" checked={form.services.dobra.enabled}
                    onChange={(e) => setSvc("dobra", "enabled", e.target.checked)} className="accent-orange-500 w-4 h-4" />
                  Dobra
                </label>
                {form.services.dobra.enabled && (
                  <div className="mt-3">
                    <label className={labelCls}>Valor por metro de dobra (R$)</label>
                    <input type="number" step="0.01" min="0" data-testid="price-svc-dobra-price_per_meter"
                      value={form.services.dobra.price_per_meter} onChange={(e) => setSvc("dobra", "price_per_meter", e.target.value)} className={inputCls} />
                  </div>
                )}
              </div>
              <div className="rounded-md border border-slate-800 p-4" data-testid="price-svc-jateamento">
                <label className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200 cursor-pointer">
                  <input type="checkbox" data-testid="price-svc-jateamento-enabled" checked={!!form.services.jateamento?.enabled}
                    onChange={(e) => setSvc("jateamento", "enabled", e.target.checked)} className="accent-orange-500 w-4 h-4" />
                  Jateamento
                </label>
                <p className="mt-2 text-[10px] text-slate-500">Apenas disponibilidade da combinação — o valor do jateamento é informado por operação.</p>
              </div>
            </div>

            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="price-input-active" checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-orange-500 w-4 h-4" />
              Configuração ativa (disponível no orçamento)
            </label>

            <button data-testid="price-save-button" disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar configuração
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
